import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/common/Icon';
import { cn } from '@/lib/utils';
import {
  resolveNotificationCategoryIcon,
  type NotificationCategory,
} from './notificationPresentation';

interface CategoryFilterChipsProps {
  categories: NotificationCategory[];
  selectedCategory: NotificationCategory | null;
  onSelectCategory: (category: NotificationCategory | null) => void;
}

export function CategoryFilterChips({
  categories,
  selectedCategory,
  onSelectCategory,
}: CategoryFilterChipsProps) {
  const { t } = useTranslation('notifications');

  return (
    <div role="group" aria-label={t('center.categories.label')} className="flex flex-wrap gap-2">
      <CategoryChip
        label={t('center.categories.all')}
        isActive={selectedCategory === null}
        onSelect={function selectAllCategories() {
          onSelectCategory(null);
        }}
      />
      {categories.map(function renderCategoryChip(category) {
        return (
          <CategoryChip
            key={category}
            label={t('center.categories.' + category)}
            iconName={resolveNotificationCategoryIcon(category)}
            isActive={selectedCategory === category}
            onSelect={function selectCategory() {
              onSelectCategory(category);
            }}
          />
        );
      })}
    </div>
  );
}

interface CategoryChipProps {
  label: string;
  iconName?: IconName;
  isActive: boolean;
  onSelect: () => void;
}

function CategoryChip({ label, iconName, isActive, onSelect }: CategoryChipProps) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onSelect}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
        isActive
          ? 'bg-zooprimary text-zoowhite shadow-sm'
          : 'bg-zoowhite border border-zooborder text-zoomuted hover:border-zooprimary hover:text-zooprimary'
      )}
    >
      {iconName === undefined ? null : <Icon name={iconName} className="text-sm" />}
      {label}
    </button>
  );
}
